import AppColors from '../constants/AppColors';
import {
  DownIcon,
  LeftIcon,
  RightIcon,
  SortIcon,
  UpIcon,
  LeftEnableIcon,
  RightEnableIcon,
} from '../constants/IconData';
import { FONT_FAMILY } from '../constants/Theme';

// ─── Default props bundle for CQTreeTable ─────────────────────────────────────
// Drop this spread directly onto <CQTreeTable {...defaultTreeTableProp} ... />
export const defaultTreeTableProp = {
  renderSortUpIcon,
  renderSortIcon,
  renderSortDownIcon,
  getCellProps,
  getHeaderCellProps,
  getRowProps,

  RightIcon: <RightIcon />,
  LeftIcon: <LeftIcon />,
};

// ─── Factory: use when you need a highlighted row ────────────────────────────
// Usage: <CQTreeTable {...createTreeTableProps(highlightedId)} ... />
export function createTreeTableProps(highlightedRowId = null) {
  return {
    renderSortUpIcon,
    renderSortIcon,
    renderSortDownIcon,
    getCellProps,
    getHeaderCellProps,
    getRowProps: (row, options) =>
      getEnhancedRowProps(row, options, highlightedRowId),

    RightIcon: <RightIcon />,
    LeftIcon: <LeftIcon />,
  };
}

// ─── Pagination props (identical shape — works for both tables) ───────────────
export function paginationProps(pagination) {
  const canGoBack = pagination?.currentPage !== 1;
  const canGoNext = pagination?.totalPage !== pagination.currentPage;

  return {
    total: pagination.totalCount,
    pageCount: pagination.totalPage,
    size: pagination.showSize,
    currentPage: pagination.currentPage,
    LeftIcon: canGoBack ? <LeftEnableIcon /> : <LeftIcon />,
    RightIcon: canGoNext ? <RightEnableIcon /> : <RightIcon />,
  };
}

// ─── Sort icons ───────────────────────────────────────────────────────────────
export function renderSortUpIcon() {
  return (
    <UpIcon
      style={{
        width: 10,
        height: 10,
        marginLeft: 1,
      }}
    />
  );
}

export function renderSortDownIcon() {
  return (
    <DownIcon
      style={{
        width: 10,
        height: 10,
        marginLeft: 1,
      }}
    />
  );
}

export function renderSortIcon() {
  return (
    <SortIcon
      style={{
        width: 15,
        height: 15,
        marginLeft: 1,
      }}
    />
  );
}

// ─── Header cell styling ──────────────────────────────────────────────────────
// Extend `startingHeaderId` / `tableHeader` with any tree-specific column ids.
export function getHeaderCellProps(header) {
  const startingHeaderId = ['assigned_auditor', 'name'];
  const tableHeader = [
    'name',
    'upload_date',
    'completed_date',
    'charts_specialty',
    'cq_score',
    'opporturnity',
  ];

  return {
    textTransform: 'none',
    fontFamily: FONT_FAMILY.ptSans,
    fontWeight: 'bold',
    fontSize: '16px',
    color: 'black',
    width: tableHeader.includes(header.id) ? '125%' : '100%',
    justifyContent: startingHeaderId.includes(header.id)
      ? 'flex-start'
      : 'center',
  };
}

// ─── Row props ────────────────────────────────────────────────────────────────
// NOTE: CQTreeTable also passes `row.depth` — you can use it here if you want
// per-depth background colours instead of (or alongside) the built-in ones.
export function getRowProps(row) {
  const bgs = ['white', '#f2faf2', '#f6f9fd', '#F5F3FF'];
  const bg  = bgs[Math.min(row.depth, bgs.length - 1)];
  return {
    style: { backgroundColor: bg }
  };
}

// ─── Cell props ───────────────────────────────────────────────────────────────
export function getCellProps(cell) {
  return {
    style: {
      fontFamily: FONT_FAMILY.ptSans,
      color: AppColors.secondary,
      fontSize: '16px',
      fontWeight: 'normal',
      textAlign: 'center',
    },
  };
}

// ─── Enhanced row props (highlighted row support) ────────────────────────────
export function getEnhancedRowProps(
  row,
  options,
  highlightedRowId = null
) {
  const baseProps = getRowProps(row);
  const isHighlighted = String(highlightedRowId) === String(row.original?.upload_id) || 
                        String(highlightedRowId) === String(row.original?.id);


  return {
    ...baseProps,
    style: {
      ...baseProps.style,
      backgroundColor: isHighlighted
        ? '#e6f0ff'
        : baseProps.style?.backgroundColor || 'inherit',
      transition: 'background-color 0.3s ease',
    },
  };
}